import { useLocales } from '@/Locales';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { Button, Modal } from '@sinohealth/butterfly-ui-components';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import styles from './index.less';

interface ProtocolModalProps {
  /** 点击取消按钮的回调 */
  onCancel?: (flag: boolean) => void;
  /** 点击同意并登录按钮的回调 */
  onConfirm?: (values: any, flag: boolean) => void;
  /** 确认按钮文本，默认为"同意并登录" */
  confirmText?: string;
  /** 取消按钮文本，默认为"取消" */
  cancelText?: string;
  /** 是否显示加载状态 */
  loading?: boolean;
}

export interface ProtocolModalRef {
  /** 打开弹窗 */
  open: (values: any) => void;
  /** 关闭弹窗 */
  close: () => void;
  /** 设置加载状态 */
  setLoading: (loading: boolean) => void;
}

/**
 * 协议确认弹窗组件
 *
 * 功能特性：
 * - 显示用户协议和隐私政策提示
 * - 支持点击链接打开协议详情
 * - 支持通过 ref 调用方式控制弹窗
 * - 可自定义按钮文本和事件处理
 * - 响应式设计，在移动端友好
 */
const ProtocolModal = forwardRef<ProtocolModalRef, ProtocolModalProps>(
  (
    {
      onCancel,
      onConfirm,
      confirmText: defaultConfirmText,
      cancelText: defaultCancelText,
      loading: defaultLoading = false,
    },
    ref,
  ) => {
    const { t } = useLocales();

    // 内部状态管理
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formValues, setFormValues] = useState<any>(null);

    useEffect(() => {
      setLoading(defaultLoading);
    }, [defaultLoading]);

    // 暴露给父组件的方法
    useImperativeHandle(
      ref,
      () => ({
        open: (val: any) => {
          setFormValues(val);
          setVisible(true);
        },
        close: () => {
          setVisible(false);
        },
        setLoading: (newLoading: boolean) => {
          setLoading(newLoading);
        },
      }),
      [onCancel, onConfirm, defaultConfirmText],
    );

    /**
     * 处理取消操作
     */
    const handleCancel = () => {
      setVisible(false);
      onCancel?.(false);
    };

    /**
     * 处理确认操作
     */
    const handleConfirm = () => {
      onConfirm?.(formValues, true);
      const timer = setTimeout(() => {
        setVisible(false);
        clearTimeout(timer);
      }, 1000);
    };

    /**
     * 打开用户服务协议
     */
    const handleOpenUserProtocol = () => {
      window.open(`${window.location.pathname}#/login/user-protocol?id=1`, '_blank');
    };

    /**
     * 打开隐私政策
     */
    const handleOpenPrivacyPolicy = () => {
      window.open(`${window.location.pathname}#/login/user-protocol?id=2`, '_blank');
    };

    return (
      <Modal
        title={null}
        centered
        open={visible}
        onCancel={handleCancel}
        footer={
          <div className={styles.buttonSection}>
            <Button onClick={handleCancel}>{defaultCancelText || t('取消')}</Button>
            <Button type="primary" onClick={handleConfirm} loading={loading}>
              {defaultConfirmText || t('同意并登录')}
            </Button>
          </div>
        }
        className={styles.protocolModal}
        maskClosable={false}
        width={500}
      >
        <div className={styles.modalContent}>
          {/* 警告图标和提示文本 */}
          <span className={styles.alertSection}>
            <ExclamationCircleFilled style={{ color: '#f2b84c' }} />
            <span className={styles.alertText}>{t('请先阅读并同意以下协议')}</span>
            {/* 协议链接区域 */}
            <span className={styles.protocolLink} onClick={handleOpenUserProtocol}>
              {t('《用户服务协议》')}
            </span>
            <span className={styles.linkSeparator}>{t('和')}</span>
            <span
              style={{ paddingRight: 8 }}
              className={styles.protocolLink}
              onClick={handleOpenPrivacyPolicy}
            >
              {t('《隐私政策》')}
            </span>
            {/* <span className={styles.protocolLinks}></span> */}
          </span>

          {/* 按钮区域 */}
        </div>
      </Modal>
    );
  },
);

ProtocolModal.displayName = 'ProtocolModal';

export default ProtocolModal;
